import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';
import { zodDeliveryNoteResponseSchema } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type DeliveryNoteAdmin } from './types';
import { zodDeliveryNoteEndpointSchema } from './validations';

/**
 * Fetches a delivery note with the given ID from the API.
 * @param deliveryNoteId - The ID of the delivery note to fetch
 * @returns The delivery note data if successful
 * @throws If the API request fails
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Asset/operation/get_delivery_note_by_id_vadmin_asset_delivery_note__id__get
 */
export const fetchDeliveryNoteAdmin = async (
  deliveryNoteId: UUID,
  options?: KyRequestOptions,
) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.DELIVERY.GET_ADMIN(deliveryNoteId), {
        validate: { pathSegments: zodDeliveryNoteEndpointSchema },
        ...options,
      })
      .json<DeliveryNoteAdmin>();

    // Validate the returned data and log any validation errors
    const validatedResponse = zodDeliveryNoteResponseSchema.safeParse(response);

    if (validatedResponse?.error?.issues?.length) {
      log.error({
        message: 'Delivery note API response has validation errors',
        error: {
          errors: validatedResponse?.error?.issues,
          errorMessage: validatedResponse?.error?.message,
          response,
        },
      });
    }

    return response;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Delivery Note Admin',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.deliveryNote.getAllAdmin.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.deliveryNote.getAllAdmin.error.generic,
            {
              ns: 'data',
            },
          );
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
